import {
    Box,
    Button,
    Chip,
    IconButton,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from "@mui/material";
import GridMediaCard from "../../components/cards/GridMediaCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import AssignmentIcon from "@mui/icons-material/Assignment";
import QueuePlayNextIcon from "@mui/icons-material/QueuePlayNext";
import AddTaskIcon from "@mui/icons-material/AddTask";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import Fetch from "../../services/Fetch";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import ReactPlayer from "react-player";
import AlertDialog from "../../components/DialogView";
import SnackbarAlert from "../../components/SnackBar";
import { useDialog } from "../../hooks/UseDialog";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage, resolveMediaUrl } from "../../helper/resolveMediaUrl";

function CourseDetails() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { open, title, description, setDialog, setOpen } = useDialog();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [tab, setTab] = useState(() => {
        const stored = parseInt(sessionStorage.getItem("content-window"), 10);
        return Number.isFinite(stored) ? stored : 0;
    });
    const [course, setCourse] = useState(null);
    const [contentId, setContentId] = useState("");
    const [imageFailed, setImageFailed] = useState(false);
    const [movingId, setMovingId] = useState("");
    const navigate = useNavigate();
    const param = useParams();

    const courseImage = resolveCourseImage(course);
    const courseName = course ? (isAr ? course.name_ar : course.name_en) : "";
    const courseDesc = course ? (isAr ? course.description_ar : course.description_en) : "";
    const categoryName = course?.category
        ? (isAr ? course.category.name_ar : course.category.name_en)
        : "";

    const orderedContents = useMemo(
        () => [...(course?.contents || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [course]
    );
    const videos = useMemo(
        () => orderedContents.filter((item) => item.content_type === "CourseFile"),
        [orderedContents]
    );
    const exams = useMemo(
        () => orderedContents.filter((item) => item.content_type === "Exam"),
        [orderedContents]
    );

    const statusLabel = (status) => {
        if (status === "accepted") return isAr ? "مقبولة" : "Approved";
        if (status === "rejected") return isAr ? "مرفوضة" : "Rejected";
        return isAr ? "بانتظار الموافقة" : "Pending approval";
    };

    const statusColor = (status) => {
        if (status === "accepted") return { bg: "#dcfce7", color: "#166534" };
        if (status === "rejected") return { bg: "#fee2e2", color: "#991b1b" };
        return { bg: "#fef3c7", color: "#92400e" };
    };

    const getCourse = useCallback(async () => {
        setGetWait(true);
        const result = await Fetch(`${host}/courses/${param.id}/show`, "GET", null);
        if (result.status === 200) {
            setCourse(result.data.data);
            setImageFailed(false);
        }
        setGetWait(false);
    }, [host, param.id, setGetWait]);

    const deleteExam = async () => {
        setSendWait(true);
        const result = await Fetch(`${host}/teacher/courses/exams/${contentId}/delete`, "DELETE", null);
        if (result.status === 200) {
            setCourse((prev) => ({
                ...prev,
                contents: prev.contents.filter((item) => item.content.id !== contentId),
            }));
            setOpen(false);
            setSnackBar("success", isAr ? "تم الحذف بنجاح" : "Deleted successfully");
            setContentId("");
        }
        setSendWait(false);
    };

    const deleteFile = async () => {
        setSendWait(true);
        const result = await Fetch(`${host}/teacher/courses/files/${contentId}/delete`, "DELETE", null);
        if (result.status === 200) {
            setCourse((prev) => ({
                ...prev,
                contents: prev.contents.filter((item) => item.content.id !== contentId),
            }));
            setOpen(false);
            setSnackBar("success", isAr ? "تم الحذف بنجاح" : "Deleted successfully");
            setContentId("");
        }
        setSendWait(false);
    };

    const moveCourseContent = async (currentContentId, contentType, direction) => {
        if (!course?.contents?.length) return;

        const typedItems = orderedContents
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.content_type === contentType);

        const currentTypedIndex = typedItems.findIndex(({ item }) => item.id === currentContentId);
        const targetTypedIndex = currentTypedIndex + direction;

        if (currentTypedIndex === -1 || targetTypedIndex < 0 || targetTypedIndex >= typedItems.length) {
            return;
        }

        const sourceIndex = typedItems[currentTypedIndex].index;
        const targetIndex = typedItems[targetTypedIndex].index;
        const reordered = [...orderedContents];
        [reordered[sourceIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[sourceIndex]];

        const normalized = reordered.map((item, index) => ({
            ...item,
            order: index + 1,
        }));
        const previousContents = course.contents;

        setMovingId(String(currentContentId));
        setCourse((prev) => ({ ...prev, contents: normalized }));

        const result = await Fetch(`${host}/teacher/courses/${param.id}/reorder-contents`, "POST", {
            content_ids: normalized.map((item) => item.id),
        });

        if (result.status !== 200) {
            setCourse((prev) => ({ ...prev, contents: previousContents }));
            setSnackBar("error", isAr ? "تعذر حفظ ترتيب المحتوى" : "Could not save content order");
        } else {
            setSnackBar("success", isAr ? "تم تحديث ترتيب المحتوى" : "Content order updated");
        }

        setMovingId("");
    };

    const handleTabChange = (_, newValue) => {
        setTab(newValue);
        sessionStorage.setItem("content-window", String(newValue));
    };

    useEffect(() => {
        getCourse();
    }, [getCourse]);

    const addVideoButton = (
        <Button
            variant="contained"
            startIcon={<QueuePlayNextIcon />}
            onClick={() => navigate(`/course-details/${param.id}/add-file`)}
            sx={{ bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
        >
            {isAr ? "إضافة ملف" : "Add file"}
        </Button>
    );

    const addExamButton = (
        <Button
            variant="contained"
            startIcon={<AddTaskIcon />}
            onClick={() => navigate(`/${param.id}/create-exam`)}
            sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
        >
            {isAr ? "إضافة اختبار" : "Add exam"}
        </Button>
    );

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                {course ? (
                    <>
                        <ScrollReveal direction="down">
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    gap: 3,
                                    mb: 3,
                                    p: 3,
                                    borderRadius: 4,
                                    bgcolor: theme.palette.background.paper,
                                    border: "1px solid",
                                    borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                    boxShadow: "0 4px 24px rgba(13,53,140,0.08)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: "100%", md: 320 },
                                        height: 200,
                                        flexShrink: 0,
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        position: "relative",
                                        bgcolor: "#0D358C",
                                    }}
                                >
                                    {courseImage && !imageFailed ? (
                                        <Box
                                            component="img"
                                            src={courseImage}
                                            alt={courseName}
                                            onError={() => setImageFailed(true)}
                                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "linear-gradient(135deg, #0D358C 0%, #2563EA 100%)",
                                            }}
                                        >
                                            <MenuBookOutlinedIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.85)" }} />
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h5" fontWeight={800} sx={{ color: "#0D358C", mb: 1 }}>
                                        {courseName}
                                    </Typography>

                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                                        {categoryName ? (
                                            <Chip size="small" label={categoryName} sx={{ bgcolor: "#eff6ff", color: "#1d4ed8" }} />
                                        ) : null}
                                        <Chip
                                            size="small"
                                            label={statusLabel(course.status)}
                                            sx={{
                                                bgcolor: statusColor(course.status).bg,
                                                color: statusColor(course.status).color,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#0D358C", mb: 0.5 }}>
                                        {isAr ? "شرح الدورة" : "Course description"}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2, whiteSpace: "pre-wrap" }}
                                    >
                                        {courseDesc?.trim() || (isAr ? "لا يوجد شرح للدورة بعد." : "No course description yet.")}
                                    </Typography>

                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                        {addVideoButton}
                                        {addExamButton}
                                    </Box>
                                </Box>
                            </Box>
                        </ScrollReveal>

                        <Box sx={{ mb: 2 }}>
                            <Tabs
                                value={tab}
                                onChange={handleTabChange}
                                sx={{
                                    "& .MuiTab-root": { fontWeight: 700, minHeight: 48 },
                                    "& .Mui-selected": { color: "#0D358C !important" },
                                    "& .MuiTabs-indicator": { bgcolor: "#0D358C", height: 3, borderRadius: 2 },
                                }}
                            >
                                <Tab icon={<OndemandVideoIcon />} iconPosition="start" label={isAr ? `ملفات الجلسات (${videos.length})` : `Lesson files (${videos.length})`} />
                                <Tab icon={<AssignmentIcon />} iconPosition="start" label={isAr ? `الاختبارات (${exams.length})` : `Exams (${exams.length})`} />
                            </Tabs>
                        </Box>

                        {tab === 0 && (
                            <DashboardPanelCard
                                title={isAr ? "ملفات جلسات الدورة" : "Course lesson files"}
                                subtitle={isAr ? "أضف فيديوهات أو ملفات PDF لطلابك" : "Add videos or PDF lesson files for your students"}
                            >
                                {videos.length === 0 ? (
                                    <Box sx={{ textAlign: "center", py: 6 }}>
                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8", mb: 1 }} />
                                        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                                            {isAr ? "لم تُضف ملفات جلسات بعد" : "No lesson files added yet"}
                                        </Typography>
                                        {addVideoButton}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        {videos.map((content) => (
                                            <Box
                                                key={content.content.id}
                                                sx={{
                                                    borderRadius: 3,
                                                    overflow: "hidden",
                                                    border: "1px solid",
                                                    borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        pt: content.content?.is_video ? "56.25%" : undefined,
                                                        minHeight: content.content?.is_video ? undefined : 220,
                                                        bgcolor: content.content?.is_video ? "#000" : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                                    }}
                                                >
                                                    <Box sx={{ position: "absolute", inset: 0 }}>
                                                        {content.content?.is_video ? (
                                                            <ReactPlayer
                                                                src={resolveMediaUrl(content.content.file_url)}
                                                                controls
                                                                width="100%"
                                                                height="100%"
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    gap: 1.5,
                                                                    p: 3,
                                                                    textAlign: "center",
                                                                }}
                                                            >
                                                                <PictureAsPdfOutlinedIcon sx={{ fontSize: 54, color: "#dc2626" }} />
                                                                <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0D358C" }}>
                                                                    {isAr ? "ملف PDF للجلسة" : "Lesson PDF file"}
                                                                </Typography>
                                                                <Button
                                                                    variant="outlined"
                                                                    startIcon={<OpenInNewOutlinedIcon />}
                                                                    onClick={() => window.open(resolveMediaUrl(content.content.file_url), "_blank", "noopener,noreferrer")}
                                                                >
                                                                    {isAr ? "فتح الملف" : "Open file"}
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ position: "absolute", top: 12, insetInlineEnd: 12, zIndex: 2, display: "flex", gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => navigate(`/course-details/${param.id}/update_file/${content.content.id}`)}
                                                            sx={{ bgcolor: "rgba(22,163,74,0.9)" }}
                                                        >
                                                            {isAr ? "تعديل" : "Edit"}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => {
                                                                setContentId(content.content.id);
                                                                setDialog(
                                                                    isAr ? "حذف الملف" : "Delete file",
                                                                    isAr ? "هل أنت متأكد من حذف هذا الملف من الدورة؟" : "Are you sure you want to delete this file from the course?"
                                                                );
                                                            }}
                                                        >
                                                            {isAr ? "حذف" : "Delete"}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                                    <Box>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {isAr ? content.content.name_ar : content.content.name_en}
                                                        </Typography>
                                                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                                                                {content.content?.is_video
                                                                    ? (isAr ? "فيديو" : "Video")
                                                                    : (isAr ? "ملف PDF" : "PDF file")}
                                                            </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isAr ? `الترتيب ${content.order ?? "—"}` : `Order ${content.order ?? "—"}`}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => moveCourseContent(content.id, "CourseFile", -1)}
                                                            disabled={videos[0]?.id === content.id || movingId === String(content.id)}
                                                        >
                                                            <ArrowUpwardOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => moveCourseContent(content.id, "CourseFile", 1)}
                                                            disabled={videos[videos.length - 1]?.id === content.id || movingId === String(content.id)}
                                                        >
                                                            <ArrowDownwardOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>{addVideoButton}</Box>
                                    </Box>
                                )}
                            </DashboardPanelCard>
                        )}

                        {tab === 1 && (
                            <DashboardPanelCard
                                title={isAr ? "اختبارات الدورة" : "Course exams"}
                                subtitle={isAr ? "أنشئ اختبارات لتقييم الطلاب" : "Create exams to assess students"}
                            >
                                {exams.length === 0 ? (
                                    <Box sx={{ textAlign: "center", py: 6 }}>
                                        <AssignmentIcon sx={{ fontSize: 56, color: "#94a3b8", mb: 1 }} />
                                        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                                            {isAr ? "لم تُضف اختبارات بعد" : "No exams added yet"}
                                        </Typography>
                                        {addExamButton}
                                    </Box>
                                ) : (
                                    <>
                                        <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {exams.map((content) => (
                                                <GridMediaCard
                                                    key={content.content.id}
                                                    title={isAr ? content.content.name_ar : content.content.name_en}
                                                    description={isAr ? content.content.description_ar : content.content.description_en}
                                                    footer={
                                                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                            {isAr ? `الترتيب ${content.order ?? "—"}` : `Order ${content.order ?? "—"}`}
                                                        </Typography>
                                                    }
                                                    actions={
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => moveCourseContent(content.id, "Exam", -1)}
                                                                disabled={exams[0]?.id === content.id || movingId === String(content.id)}
                                                            >
                                                                <ArrowUpwardOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => moveCourseContent(content.id, "Exam", 1)}
                                                                disabled={exams[exams.length - 1]?.id === content.id || movingId === String(content.id)}
                                                            >
                                                                <ArrowDownwardOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                            <Button
                                                                size="small"
                                                                onClick={() => navigate(`/courses/${course.id}/exams/${content.content.id}/questions`)}
                                                            >
                                                                {isAr ? "الأسئلة" : "Questions"}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                onClick={() => navigate(`/course/${param.id}/update-exam/${content.content.id}`)}
                                                            >
                                                                {isAr ? "تعديل" : "Update"}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                    setContentId(content.content.id);
                                                                    setDialog(
                                                                        isAr ? "حذف الاختبار" : "Delete exam",
                                                                        isAr ? "هل أنت متأكد من حذف هذا الاختبار؟" : "Are you sure you want to delete this exam?"
                                                                    );
                                                                }}
                                                            >
                                                                {isAr ? "حذف" : "Delete"}
                                                            </Button>
                                                        </>
                                                    }
                                                />
                                            ))}
                                        </Box>
                                        <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>{addExamButton}</Box>
                                    </>
                                )}
                            </DashboardPanelCard>
                        )}
                    </>
                ) : (
                    !getWait && (
                        <Typography sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                            {isAr ? "تعذر تحميل الدورة" : "Could not load course"}
                        </Typography>
                    )
                )}
            </PageContent>

            <AlertDialog
                title={title}
                description={description}
                onCancel={() => setOpen(false)}
                onConfirm={() => (tab === 1 ? deleteExam() : deleteFile())}
                wait={sendWait}
                openDialog={open}
            />
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default CourseDetails;
